import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Search, ShieldCheck, XCircle, CalendarClock, FileSpreadsheet, FileText } from "lucide-react";
import { DateField } from "@/components/common/DateField";
import { formatDate, isoFromDisplay } from "@/lib/dateFormat";

const TABS = [["retirement", "Retirement forecast"], ["seniority", "Seniority"], ["posting", "Posting duration"], ["completeness", "Completeness & pending"], ["member", "Edit a member"], ["settings", "Settings"]];
const LOCKABLE = ["date_of_birth", "date_of_joining_service", "joining_advertisement_no", "joining_merit_no", "original_merit_no", "batch", "date_of_current_posting"];
const dl = (blob, name) => { const u = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = u; a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(u), 10000); };
const dateStr = (iso) => formatDate(iso) || "—";

export const ServiceSection = () => {
  const [tab, setTab] = useState("retirement");
  const [districts, setDistricts] = useState([]);
  const [district, setDistrict] = useState("all");
  useEffect(() => { api.districts().then(setDistricts); }, []);

  return (
    <div>
      <h2 className="mb-4 font-serif text-2xl font-semibold text-gmoaa-green">Service Details</h2>
      <div className="mb-5 flex flex-wrap gap-2">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`svc-tab-${k}`} onClick={() => setTab(k)} className={`rounded-full px-3 py-1.5 text-sm font-medium ${tab === k ? "bg-gmoaa-green text-white" : "bg-gmoaa-surface text-gmoaa-text-dark hover:bg-gmoaa-green-light"}`}>{l}</button>
        ))}
      </div>
      {tab !== "member" && tab !== "settings" && (
        <div className="mb-4 w-56">
          <Select value={district} onValueChange={setDistrict}>
            <SelectTrigger className="border-gmoaa-border-subtle bg-white"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All districts</SelectItem>{districts.map((d) => <SelectItem key={d.id} value={d.slug}>{d.name_en}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      {tab === "retirement" && <Retirement district={district} />}
      {tab === "seniority" && <Seniority district={district} />}
      {tab === "posting" && <Posting district={district} />}
      {tab === "completeness" && <Completeness />}
      {tab === "member" && <MemberEditor districts={districts} />}
      {tab === "settings" && <Settings />}
    </div>
  );
};

const Retirement = ({ district }) => {
  const [data, setData] = useState(null);
  useEffect(() => { adminApi.retirementForecast(district).then(setData); }, [district]);
  if (!data) return <p className="text-gmoaa-text-muted">Loading…</p>;
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button size="sm" variant="outline" data-testid="export-retirement-xlsx" onClick={() => adminApi.exportRetirement("xlsx", district).then((b) => dl(b, "retirement.xlsx"))}><FileSpreadsheet size={14} className="mr-1" /> Excel</Button>
        <Button size="sm" variant="outline" onClick={() => adminApi.exportRetirement("pdf", district).then((b) => dl(b, "retirement.pdf"))}><FileText size={14} className="mr-1" /> PDF</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {data.by_year.map((y) => <div key={y.year} className="rounded-lg border border-gmoaa-border-subtle bg-white px-3 py-2 text-center"><p className="font-serif text-lg font-bold text-gmoaa-green">{y.count}</p><p className="text-xs text-gmoaa-text-muted">{y.year}</p></div>)}
        {data.by_year.length === 0 && <p className="text-sm text-gmoaa-text-muted">No retirements in the next 10 years (add members' dates of birth).</p>}
      </div>
      <div>
        <p className="mb-2 text-sm font-semibold text-gmoaa-green">Retiring in the next 12 months ({data.next_12_months.length})</p>
        {data.next_12_months.map((r) => <div key={r.id} className="flex justify-between border-b border-gmoaa-border-subtle py-1.5 text-sm"><span>{r.name_en} · {r.district}</span><span className="text-gmoaa-saffron-hover">{dateStr(r.retirement_date)}</span></div>)}
      </div>
    </div>
  );
};

const Seniority = ({ district }) => {
  const [rows, setRows] = useState([]);
  useEffect(() => { adminApi.seniority(district).then(setRows); }, [district]);
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Button size="sm" variant="outline" data-testid="export-seniority-xlsx" onClick={() => adminApi.exportSeniority("xlsx", district).then((b) => dl(b, "seniority.xlsx"))}><FileSpreadsheet size={14} className="mr-1" /> Excel</Button>
        <Button size="sm" variant="outline" onClick={() => adminApi.exportSeniority("pdf", district).then((b) => dl(b, "seniority.pdf"))}><FileText size={14} className="mr-1" /> PDF</Button>
      </div>
      <p className="text-xs text-gmoaa-saffron-hover">Indicative only; the official list is issued by the department.</p>
      {rows.map((r, i) => <div key={r.id} className="flex justify-between border-b border-gmoaa-border-subtle py-1.5 text-sm"><span>{i + 1}. {r.name_en} · {r.district}</span><span className="text-gmoaa-text-muted">join {dateStr(r.date_of_joining_service)} · merit {r.joining_merit_no || "—"}</span></div>)}
      {rows.length === 0 && <p className="text-sm text-gmoaa-text-muted">No members with a joining date yet.</p>}
    </div>
  );
};

const Posting = ({ district }) => {
  const [data, setData] = useState(null);
  useEffect(() => { adminApi.postingDuration(district).then(setData); }, [district]);
  if (!data) return null;
  return (
    <div className="space-y-2">
      <p className="text-sm text-gmoaa-text-muted">Members at the current place for more than {data.n} years:</p>
      {data.rows.map((r) => <div key={r.id} className="flex justify-between border-b border-gmoaa-border-subtle py-1.5 text-sm"><span>{r.name_en} · {r.district}</span><span className="text-gmoaa-text-muted">{r.duration.years}y {r.duration.months}m</span></div>)}
      {data.rows.length === 0 && <p className="text-sm text-gmoaa-text-muted">None over {data.n} years.</p>}
    </div>
  );
};

const Completeness = () => {
  const [data, setData] = useState(null);
  useEffect(() => { adminApi.completeness().then(setData); }, []);
  if (!data) return null;
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <div>
        <p className="mb-2 text-sm font-semibold text-gmoaa-green">Completeness by district</p>
        {data.per_district.map((d) => <div key={d.district} className="flex justify-between border-b border-gmoaa-border-subtle py-1.5 text-sm"><span>{d.district}</span><span className="text-gmoaa-text-muted">{d.filled}/{d.total} ({d.percent}%)</span></div>)}
      </div>
      <div>
        <p className="mb-2 text-sm font-semibold text-gmoaa-saffron-hover">Pending verification ({data.pending_queue.length})</p>
        {data.pending_queue.map((p) => <div key={p.id} className="border-b border-gmoaa-border-subtle py-1.5 text-sm"><span className="font-medium">{p.name_en}</span> <span className="text-xs text-gmoaa-text-muted">· {p.district} · {p.fields.join(", ")}</span></div>)}
        {data.pending_queue.length === 0 && <p className="text-sm text-gmoaa-text-muted">Nothing pending.</p>}
      </div>
    </div>
  );
};

const MemberEditor = ({ districts }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [sel, setSel] = useState(null);
  const [svc, setSvc] = useState(null);
  const [form, setForm] = useState({});

  const search = async (val) => { setQ(val); if (val.trim().length < 2) { setResults([]); return; } setResults(await adminApi.members({ q: val })); };
  const load = async (m) => {
    setSel(m); const d = await adminApi.getMemberService(m.id); setSvc(d);
    setForm({ date_of_birth: isoFromDisplay(d.date_of_birth_display) || "", date_of_joining_service: isoFromDisplay(d.date_of_joining_service_display) || "", joining_advertisement_no: d.joining_advertisement_no || "", joining_merit_no: d.joining_merit_no || "", original_merit_no: d.original_merit_no || "", batch: d.batch || "", date_of_current_posting: isoFromDisplay(d.date_of_current_posting_display) || "" });
  };
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const save = async () => { try { const d = await adminApi.putMemberService(sel.id, form); setSvc(d); toast.success("Saved & logged"); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const verify = async () => { await adminApi.verifyService(sel.id, LOCKABLE); toast.success("Verified"); load(sel); };
  const reject = async () => { const r = window.prompt("Rejection reason:"); if (!r) return; await adminApi.rejectService(sel.id, LOCKABLE, r); toast.success("Rejected"); load(sel); };

  return (
    <div>
      <div className="relative mb-3 max-w-md">
        <Search size={15} className="absolute left-3 top-2.5 text-gmoaa-text-muted" />
        <Input data-testid="svc-member-search" value={q} onChange={(e) => search(e.target.value)} placeholder="Search member by name or ID…" className="border-gmoaa-border-subtle bg-white pl-9" />
        {results.length > 0 && !sel && <div className="mt-1 max-h-48 overflow-y-auto rounded-lg border border-gmoaa-border-subtle bg-white">{results.map((m) => <button key={m.id} onClick={() => load(m)} className="block w-full px-3 py-2 text-left text-sm hover:bg-gmoaa-green-light/50">{m.name_en} · {m.member_id} · {m.district_slug}</button>)}</div>}
      </div>
      {sel && svc && (
        <div data-testid="svc-admin-editor" className="rounded-xl border border-gmoaa-border-subtle bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="font-semibold text-gmoaa-green">{sel.name_en} <span className="text-xs text-gmoaa-text-muted">({sel.member_id})</span></p>
            <button className="text-xs text-gmoaa-red" onClick={() => { setSel(null); setSvc(null); setQ(""); }}>Close</button>
          </div>
          {svc.computed?.retirement_date && <p className="mb-3 flex items-center gap-1 text-sm font-medium text-gmoaa-green"><CalendarClock size={15} /> Retires {dateStr(svc.computed.retirement_date)} (age {svc.computed.retirement_age})</p>}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {[["date_of_birth", "Date of birth", true], ["date_of_joining_service", "Joining date", true], ["joining_advertisement_no", "Advertisement no.", false], ["batch", "Batch", false], ["joining_merit_no", "Joining merit no.", false], ["original_merit_no", "Original merit no.", false], ["date_of_current_posting", "Current posting date", true]].map(([k, l, isDate]) => (
              <div key={k}><Label className="mb-0.5 block text-xs text-gmoaa-text-muted">{l} {svc.verification?.[k]?.status && <span className="text-[10px] text-gmoaa-saffron-hover">({svc.verification[k].status})</span>}</Label>
                {isDate
                  ? <DateField value={form[k] || ""} onChange={set(k)} testid={`svc-admin-${k}`} disableFuture={k === "date_of_birth"} />
                  : <Input data-testid={`svc-admin-${k}`} value={form[k] || ""} onChange={set(k)} className="border-gmoaa-border-subtle bg-white" />}
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" data-testid="svc-admin-save" onClick={save} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save (logged)</Button>
            <Button size="sm" variant="outline" data-testid="svc-admin-verify" onClick={verify} className="border-gmoaa-green text-gmoaa-green"><ShieldCheck size={14} className="mr-1" /> Verify all</Button>
            <Button size="sm" variant="outline" onClick={reject} className="border-gmoaa-red text-gmoaa-red"><XCircle size={14} className="mr-1" /> Reject</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const Settings = () => {
  const [s, setS] = useState(null);
  useEffect(() => { adminApi.getServiceSettings().then(setS); }, []);
  if (!s) return null;
  const save = async () => { await adminApi.putServiceSettings(s); toast.success("Settings saved"); };
  return (
    <div className="max-w-md space-y-3 rounded-xl border border-gmoaa-border-subtle bg-white p-4">
      <div><Label className="mb-1 block text-xs text-gmoaa-text-muted">Retirement age</Label><Input type="number" data-testid="svc-retire-age" value={s.retire_age} onChange={(e) => setS({ ...s, retire_age: Number(e.target.value) })} className="border-gmoaa-border-subtle" /></div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={s.first_prev_month} onChange={(e) => setS({ ...s, first_prev_month: e.target.checked })} /> Treat DOB on the 1st as retiring at the end of the previous month</label>
      <div><Label className="mb-1 block text-xs text-gmoaa-text-muted">Posting-duration threshold (years)</Label><Input type="number" value={s.posting_n} onChange={(e) => setS({ ...s, posting_n: Number(e.target.value) })} className="border-gmoaa-border-subtle" /></div>
      <Button onClick={save} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save settings</Button>
    </div>
  );
};
